import { FC, FormEvent, useState, useRef } from 'react';
import styled from 'styled-components';
import emailjs from '@emailjs/browser';
import { useTranslation } from 'react-i18next';

const ContactSection = styled.section`
  background-color: #000;
  color: #fff;
  padding: 80px 0;
  position: relative;
  overflow: hidden;

  &:before {
    content: '{ }';
    position: absolute;
    font-size: 600px;
    opacity: 0.08;
    font-family: var(--secondary-font-family);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(-15deg);
    pointer-events: none;
    font-weight: bold;
    letter-spacing: -20px;
  }
`;

const ContactForm = styled.form`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
  z-index: 1;

  .input-group {
    margin-bottom: 20px;
    position: relative;
  }

  label {
    display: block;
    margin-bottom: 8px;
    font-family: var(--secondary-font-family);
    color: #888;
    font-size: 14px;
  }

  input, textarea {
    width: 100%;
    background: #111;
    border: 1px solid #333;
    padding: 12px;
    color: #fff;
    font-family: var(--secondary-font-family);
    font-size: 16px;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: #666;
      box-shadow: 0 0 0 2px rgba(255,255,255,0.1);
    }
  }

  textarea {
    min-height: 150px;
    resize: vertical;
  }

  .submit-btn {
    background: transparent;
    border: 1px solid #fff;
    color: #fff;
    padding: 12px 30px;
    font-family: var(--secondary-font-family);
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &:hover {
      background: #fff;
      color: #000;
    }
  }
`;

const Contact: FC = () => {
  const { t } = useTranslation();
  const form = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.current) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      await emailjs.sendForm(
        'service_plamq8d', // 'YOUR_SERVICE_ID',
        'template_yy4geft', // 'YOUR_TEMPLATE_ID',
        form.current,
        'zd8TF6WlBmuZGC7fZ', // 'YOUR_PUBLIC_KEY'
      );
      setSubmitStatus('success');
      if (form.current) form.current.reset();
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error sending email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection id="contact">
      <div className="wrapper">
        <h2>{t('contact.title')}</h2>
        <p>{t('contact.subtitle')}</p>
        
        <ContactForm ref={form} onSubmit={handleSubmit}>
          <div className="input-group">
            <label>{t('contact.label_name')}</label>
            <input 
              type="text"
              name="user_name"
              required
              placeholder={t('contact.input_name')}
            />
          </div>

          <div className="input-group">
            <label>{t('contact.label_email')}</label>
            <input 
              type="email"
              name="user_email"
              required
              placeholder={t('contact.input_email')}
            />
          </div>

          <div className="input-group">
            <label>{t('contact.label_message')}</label>
            <textarea 
              name="message"
              required
              placeholder={t('contact.input_message')}
            />
          </div>

          <button type="submit" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? t('contact.submitting') : t('contact.action_button')}
          </button>

          {submitStatus === 'success' && (
            <p style={{ color: '#4CAF50', marginTop: '1rem' }}>
              {t('contact.success')}
            </p>
          )}
          {submitStatus === 'error' && (
            <p style={{ color: '#f44336', marginTop: '1rem' }}>
              {t('contact.error')}
            </p>
          )}
        </ContactForm>
      </div>
    </ContactSection>
  );
};

export default Contact;